'use client';

/**
 * Right-hand header cluster for signed-in users: round support + notification
 * buttons and the avatar account menu. Shared by AppNavbar and MainNavbar.
 */
import { useSyncExternalStore } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import {
  Bell,
  BellRing,
  ChevronDown,
  CreditCard,
  Flame,
  Headset,
  LogOut,
  Sparkles,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';

import { usePushNotifications } from '~/lib/hooks/use-push-notifications';
import { safeLocalStorage } from '~/lib/safe-storage';

const NAV_CSS = `
.nav-round {
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; flex-shrink: 0; border-radius: 9999px;
  color: rgba(255,255,255,0.9);
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.03);
  transition: border-color .2s ease, background .2s ease, color .2s ease, box-shadow .2s ease;
  -webkit-tap-highlight-color: transparent;
}
.nav-round:hover, .nav-round[data-state="open"] {
  color: #fff;
  border-color: rgba(236,72,153,0.55);
  background: rgba(236,72,153,0.10);
  box-shadow: 0 0 18px -6px rgba(236,72,153,0.8);
}
.nav-round:focus-visible { outline: 2px solid rgba(236,72,153,0.7); outline-offset: 2px; }
.nav-avatar {
  display: inline-flex; align-items: center; gap: 10px; flex-shrink: 0;
  border-radius: 9999px; color: rgba(255,255,255,0.75);
  -webkit-tap-highlight-color: transparent;
}
.nav-avatar__img {
  position: relative; display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; overflow: hidden; border-radius: 9999px;
  background: rgba(255,255,255,0.06);
  box-shadow: 0 0 0 2px rgba(244,114,182,0.75);
  transition: box-shadow .2s ease;
}
.nav-avatar:hover .nav-avatar__img, .nav-avatar[data-state="open"] .nav-avatar__img {
  box-shadow: 0 0 0 2px #ec4899, 0 0 18px -4px rgba(236,72,153,0.9);
}
.nav-avatar:hover, .nav-avatar[data-state="open"] { color: #fff; }
.nav-avatar svg.nav-chevron { transition: transform .2s ease; }
.nav-avatar[data-state="open"] svg.nav-chevron { transform: rotate(180deg); }
.nav-avatar:focus-visible { outline: none; }
.nav-avatar:focus-visible .nav-avatar__img { box-shadow: 0 0 0 2px #fff; }
.nav-divider { width: 1px; height: 32px; flex-shrink: 0; background: rgba(255,255,255,0.14); }
.nav-item { display: flex; align-items: center; gap: 12px; width: 100%;
  border-radius: 12px; padding: 10px 12px; font-size: 14px; text-align: left;
  color: rgba(255,255,255,0.88); cursor: pointer; }
.nav-item:hover, .nav-item:focus, .nav-item[data-highlighted] {
  outline: none; color: #fff; background: rgba(236,72,153,0.14); }
.nav-item svg { width: 18px; height: 18px; color: #f472b6; flex-shrink: 0; }
.nav-note { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); padding: 12px; }
@media (max-width: 1023px) { .nav-round { width: 38px; height: 38px; } }
/* Phones: the first-purchase countdown pill needs the wordmark's room. */
@media (max-width: 419px) {
  body:has([data-test="first-purchase-pill"]) .nav-wordmark { display: none; }
}
`;

// Inline so the shadcn content classes (bg-popover, z-50) cannot win.
const PANEL_STYLE = {
  zIndex: 130,
  borderRadius: 18,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(24,16,28,0.97)',
  backdropFilter: 'blur(14px)',
  boxShadow: '0 24px 48px -16px rgba(0,0,0,0.75)',
  color: '#fff',
};

export function NavbarStyles() {
  return <style>{NAV_CSS}</style>;
}

export function NavDivider() {
  return <span aria-hidden className="nav-divider" />;
}

export function SupportButton() {
  const { t } = useTranslation('common');

  return (
    <Link
      href="/contact"
      className="nav-round"
      aria-label={t('navbar.support')}
      title={t('navbar.support')}
      data-test="navbar-support"
    >
      <Headset className="h-5 w-5" />
    </Link>
  );
}

// The dot marks something new since the bell was last opened. The daily
// reward resets every day, so the dot comes back once a day.
const BELL_SEEN_KEY = 'edem-bell-seen';
const today = () => new Date().toISOString().slice(0, 10);
const bellListeners = new Set<() => void>();

function subscribeBell(listener: () => void) {
  bellListeners.add(listener);
  return () => bellListeners.delete(listener);
}

function markBellSeen() {
  safeLocalStorage.setItem(BELL_SEEN_KEY, today());
  bellListeners.forEach((listener) => listener());
}

export function NotificationsBell() {
  const { t } = useTranslation('common');
  // Server render has no storage: treat as seen so markup matches.
  const seen = useSyncExternalStore(
    subscribeBell,
    () => safeLocalStorage.getItem(BELL_SEEN_KEY) === today(),
    () => true,
  );
  const { isSupported, isSubscribed, subscribe } = usePushNotifications();
  const canEnablePush = isSupported && !isSubscribed;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open && !seen) markBellSeen();
      }}
    >
      <PopoverTrigger
        className="nav-round relative"
        aria-label={t('navbar.notifications')}
        title={t('navbar.notifications')}
        data-test="navbar-notifications"
      >
        <Bell className="h-5 w-5" />
        {!seen && (
          <span
            aria-hidden
            className="absolute"
            style={{
              top: 9,
              right: 10,
              width: 9,
              height: 9,
              borderRadius: 9999,
              background: '#f472b6',
              boxShadow: '0 0 0 2px #0b0710',
            }}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        collisionPadding={12}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="nav-panel p-2"
        style={{ ...PANEL_STYLE, width: 'min(320px, calc(100vw - 24px))' }}
      >
        <p className="px-2 pt-1.5 pb-2.5 text-sm font-semibold text-white">
          {t('navbar.notifications')}
        </p>
        <div className="flex flex-col" style={{ gap: 8 }}>
          <Link href="/challenges" className="nav-item nav-note">
            <Flame />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">
                {t('navbar.dailyRewardTitle')}
              </span>
              <span className="block text-xs text-white/55">
                {t('navbar.dailyRewardText')}
              </span>
            </span>
          </Link>
          {canEnablePush && (
            <button
              type="button"
              className="nav-item nav-note"
              onClick={() => void subscribe()}
            >
              <BellRing />
              <span className="min-w-0 flex-1">
                <span className="block font-medium">
                  {t('navbar.enablePushTitle')}
                </span>
                <span className="block text-xs text-white/55">
                  {t('navbar.enablePushText')}
                </span>
              </span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function avatarFromMetadata(metadata?: Record<string, unknown>) {
  const candidate = metadata?.avatar_url ?? metadata?.picture;
  return typeof candidate === 'string' && candidate.length > 0
    ? candidate
    : null;
}

export function AccountMenu({
  userMetadata,
  onSignOut,
}: {
  userMetadata?: Record<string, unknown>;
  onSignOut: () => void;
}) {
  const { t } = useTranslation('common');
  const avatarUrl = avatarFromMetadata(userMetadata);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className="nav-avatar"
        aria-label={t('navbar.myAccountAria')}
        data-test="navbar-profile-button"
      >
        <span className="nav-avatar__img">
          {avatarUrl ? (
            <Image
              src={avatarUrl}
              alt=""
              width={44}
              height={44}
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
              unoptimized
            />
          ) : (
            <User className="h-5 w-5 text-white/85" />
          )}
        </span>
        <ChevronDown className="nav-chevron hidden h-5 w-5 sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="nav-panel w-56 p-2"
        style={PANEL_STYLE}
      >
        <DropdownMenuItem asChild className="nav-item">
          <Link href="/account">
            <User />
            {t('navbar.myProfile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="nav-item">
          <Link href="/my-ai/characters">
            <Sparkles />
            {t('navbar.myCharacters')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="nav-item">
          <Link href="/account/billing">
            <CreditCard />
            {t('navbar.billing')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 bg-white/10" />
        <DropdownMenuItem
          className="nav-item"
          onSelect={onSignOut}
          data-test="navbar-sign-out"
        >
          <LogOut />
          {t('navbar.signOutAria')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
